import React, { useEffect } from 'react';

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Welcome to ViSRUPT – Terms of Service & Legal Policies</h1>
          <p className="text-slate-300 text-lg">Please review these terms carefully before using our services</p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">1. Introduction</h2>
              <p className="text-slate-700 leading-relaxed">Welcome to <strong>ViSRUPT</strong>! We're glad to have you here. Our mission is to provide <strong>custom algorithmic solutions</strong>, <strong>automation tools</strong>, and <strong>software development</strong> tailored to your needs.</p>
              <p className="text-slate-700 leading-relaxed">Before using our services, please take a moment to review our terms. These are designed to ensure <strong>transparency</strong>, define our scope of work, and clarify our responsibilities. Our goal is to deliver <strong>reliable, high-quality solutions</strong>, and we appreciate your trust in us.</p>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">2. Understanding Our Services</h2>
              <p className="text-slate-700 leading-relaxed">At ViSRUPT, we specialize in developing software solutions for <strong>trading automation</strong>, <strong>algorithmic strategies</strong>, and <strong>workflow automation</strong>. However, it's important to understand that:</p>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li>We are <strong>software developers</strong>, not financial advisors or investment managers.</li>
                <li>Our role is to build <strong>custom tools</strong> based on your specifications—we do not provide investment advice or trading recommendations.</li>
                <li>The effectiveness of any strategy depends on <strong>market conditions</strong>, your trading approach, and third-party infrastructure, which are beyond our control.</li>
              </ul>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">3. No Guarantee of Profits</h2>
              <p className="text-slate-700 leading-relaxed">We take pride in developing high-quality software, but it's important to note:</p>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li>We do <strong>not guarantee</strong> that any algorithm, bot, or automation tool will generate profits or prevent losses.</li>
                <li>The performance of any strategy is <strong>solely dependent</strong> on your trading decisions and market conditions.</li>
                <li>We encourage clients to <strong>thoroughly test</strong> their strategies and implement appropriate risk management practices.</li>
              </ul>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">4. Third-Party Services & Broker APIs</h2>
              <p className="text-slate-700 leading-relaxed">Many of our solutions integrate with <strong>broker APIs</strong>, market data providers, and external platforms. While we ensure compatibility, we cannot guarantee the performance of third-party services.</p>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li><strong>Broker outages</strong>, API limits, execution delays, or incorrect data are beyond our control.</li>
                <li>We recommend that clients stay informed about their broker's policies and service uptime.</li>
              </ul>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">5. Software Reliability & Client Responsibilities</h2>
              <p className="text-slate-700 leading-relaxed">We strive to provide <strong>robust and well-tested solutions</strong>, but like any technology, certain factors may impact performance, such as:</p>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li>Unexpected broker API failures</li>
                <li>Internet or server downtimes</li>
                <li>Market anomalies or high volatility</li>
                <li>Client-side hardware or software issues</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-6">To ensure smooth operations, we encourage clients to:</p>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li>Implement <strong>fail-safes</strong> such as stop-loss mechanisms and monitoring systems.</li>
                <li>Maintain <strong>backup copies</strong> of critical data and configurations.</li>
              </ul>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">6. Limitation of Liability</h2>
              <p className="text-slate-700 leading-relaxed">At ViSRUPT, we value transparency. While we stand by the quality of our work, we want to be upfront about the limitations:</p>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li>We are <strong>not liable</strong> for financial losses, missed trading opportunities, or technical malfunctions.</li>
                <li>We do not entertain claims related to profit or loss, execution delays, or third-party service disruptions.</li>
                <li>Clients use our software at their own risk, and we encourage thorough testing before live deployment.</li>
              </ul>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">7. Data Security & Confidentiality</h2>
              <p className="text-slate-700 leading-relaxed">Your privacy matters to us. While we do not store or manage your trading data unless explicitly agreed upon, we maintain <strong>strict confidentiality</strong> regarding all client projects. However, we encourage clients to:</p>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li>Use <strong>secure authentication</strong> practices when using third-party APIs.</li>
                <li>Maintain their own data backups to prevent loss due to unforeseen circumstances.</li>
              </ul>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">8. No Resale or Unauthorized Distribution</h2>
              <p className="text-slate-700 leading-relaxed">All algorithms, automated tools, and custom solutions developed by ViSRUPT are <strong>exclusively</strong> for the client they were built for. Clients cannot resell, redistribute, sublicense, or share our solutions with third parties without prior written approval from us.</p>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li>If we discover that a client has resold, transferred, or distributed our software without consent, we reserve the right to:</li>
                <ul className="list-circle pl-6 space-y-2 mt-2">
                  <li><strong>Immediately discontinue</strong> business with the client.</li>
                  <li><strong>Revoke</strong> customer support, updates, and any further enhancements for all software provided to that client.</li>
                </ul>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">We take this policy seriously to maintain the integrity and exclusivity of our services.</p>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">9. Service Modifications & Account Termination</h2>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li>We continuously improve our services and may introduce updates from time to time.</li>
                <li>If a client violates our terms or engages in misuse of our software, we reserve the right to <strong>suspend or terminate</strong> services.</li>
              </ul>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">10. Resolving Issues & Support</h2>
              <p className="text-slate-700 leading-relaxed">We value our clients and are always here to assist you. If you encounter any issues or have concerns, please reach out to us.</p>
              <ul className="list-disc pl-6 space-y-3 text-slate-700">
                <li>We prefer to resolve matters through <strong>open communication</strong> and mutual understanding.</li>
                <li>For complex issues, we'll work together to find the best possible solution.</li>
              </ul>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">11. Agreement & Future Updates</h2>
              <p className="text-slate-700 leading-relaxed">By using our services, you acknowledge and accept these terms. We may update our policies over time, and we'll make sure to inform our clients of any changes.</p>
              <p className="text-slate-700 leading-relaxed">If you have any questions or need clarification, feel free to contact us at <a href="mailto:info@ViSRUPT.in" className="text-blue-600 hover:text-blue-800">info@ViSRUPT.in</a>.</p>
              <p className="text-slate-700 leading-relaxed font-medium mt-8">Thank you for choosing ViSRUPT! We look forward to working with you and providing solutions that help you achieve your goals.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;